import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { SimpleSelect } from 'react-selectize-advizr';
import has from 'lodash/has';

import { isEmpty, isNumeric, isNonEmptyString } from '../../../js/app/react/validators';
import withResponsiveness from '../../CommonComponents/withResponsiveness';

export const LEFT = 1;
export const CENTER = 2;
export const RIGHT = 3;

export function determineInputHTMLClass(focused, isUnder, focusedClassName, alignment, className) {
  const classNames = [
    'no-selection',
    'adv-workflow-input-placeholder',
    isUnder ? 'adv-workflow-input-placeholder-under' : 'adv-workflow-input-placeholder-over',
  ];

  if (isNonEmptyString(className)) {
    classNames.push(className.trim().split(/\s+/));
  }

  const ALIGNMENT_CLASSNAME_MAP = {
    [LEFT]: 'adv-workflow-input-placeholder-left',
    [CENTER]: 'adv-workflow-input-placeholder-center',
    [RIGHT]: 'adv-workflow-input-placeholder-right',
  };

  const FOCUSED_CLASSNAME_MAP = {
    [false]: '',
    [true]: [focusedClassName],
  };

  if (!has(ALIGNMENT_CLASSNAME_MAP, alignment)) {
    throw Error('Unexpected alignment');
  }

  if (!has(FOCUSED_CLASSNAME_MAP, focused)) {
    throw Error('Unexpected value for focus');
  }

  return classNames
    .concat(ALIGNMENT_CLASSNAME_MAP[alignment])
    .concat(FOCUSED_CLASSNAME_MAP[focused])
    .join(' ');
}

function determineInputHTMLStyle(alignment, paddingLeft, paddingRight) {
  switch (alignment) {
    case LEFT:
      if (paddingLeft === null) {
        return {};
      }
      return {
        left: `${paddingLeft}px`,
      };
    case CENTER:
      return {
        left: '50%',
      };
    case RIGHT:
      if (paddingRight === null) {
        return {};
      }
      return {
        right: `${paddingRight}px`,
      };
    default:
      throw Error('Unexpected alignment');
  }
}

const EMPTY_HANDLER = () => {};

class WorkflowSimpleSelect extends Component {
  constructor() {
    super();
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onInputRef = this.onInputRef.bind(this);
    this.onPostRender = this.onPostRender.bind(this);
    this.onNativeSelectOptionChanged = this.onNativeSelectOptionChanged.bind(this);
    this.onNativeSelectRef = this.onNativeSelectRef.bind(this);
    this.state = {
      paddingRight: null,
      paddingLeft: null,
    };
  }

  componentDidMount() {
    this.onPostRender();
  }

  componentDidUpdate() {
    this.onPostRender();
  }

  onFocus(event) {
    this.props.onFocus(event);
  }

  onBlur(event) {
    this.props.onBlur(event);
  }

  onInputRef(inputElement) {
    this.inputElement = inputElement;
    const { innerRef } = this.props;
    innerRef(inputElement);
  }

  onPostRender() {
    const { ADV } = window;

    if (this.selectDOM) {
      if (this.props.value === null) {
        this.selectDOM.selectedIndex = -1;
      }
    }

    if (this.inputElement) {
      const newPaddingRight = parseFloat(ADV.util.getCss(this.inputElement, 'paddingRight'));
      if (isNumeric(newPaddingRight)) {
        if (this.state.paddingRight !== newPaddingRight) {
          this.setState({
            paddingRight: newPaddingRight,
          });
        }
      }
      const newPaddingLeft = parseFloat(ADV.util.getCss(this.inputElement, 'paddingLeft'));
      if (isNumeric(newPaddingLeft)) {
        if (this.state.paddingLeft !== newPaddingLeft) {
          this.setState({
            paddingLeft: newPaddingLeft,
          });
        }
      }
    }
  }

  onNativeSelectRef(selectDOM) {
    this.selectDOM = selectDOM;
  }

  onNativeSelectOptionChanged({ target: { value: searchValue } }) {
    const { options, onValueChange } = this.props;
    const optionObject = options.find(({ value }) => value.toString() === searchValue.toString());

    onValueChange(optionObject);
  }

  getNativeGroupedOptions() {
    const { options, groups: groupsProps } = this.props;
    const groupsMap = {};
    groupsProps.forEach(({ groupId, ...rest }) => {
      groupsMap[groupId] = {
        ...rest,
        options: [],
      };
    });
    options.forEach(option => {
      groupsMap[option.groupId].options.push(option);
    });
    return Object.keys(groupsMap)
      .map(groupId => ({ ...groupsMap[groupId], groupId }))
      .filter(({ options: groupOptions }) => groupOptions.length);
  }

  getNativeSelectValueProps() {
    const { value } = this.props;
    if (value === null) {
      return {};
    }
    return { value: value.value };
  }

  render() {
    const {
      value,
      placeholder,
      className,
      placeholderClassName,
      alignment,
      focusedClassName,
      options,
      groups,
      onValueChange,
      isLeftAligned,
      isUnder,
      id,
      viewport: { isMobile },
    } = this.props;
    const { paddingRight, paddingLeft } = this.state;
    const computedAlignment = alignment || (isLeftAligned ? LEFT : CENTER);

    return (
      <div className="adv-workflow-simple-select">
        <div
          className={determineInputHTMLClass(
            !isEmpty(value),
            isUnder,
            focusedClassName,
            computedAlignment,
            placeholderClassName,
          )}
          style={determineInputHTMLStyle(computedAlignment, paddingLeft, paddingRight)}
          aria-label={placeholder}
          id={id}
        >
          {placeholder}
        </div>
        <SimpleSelect
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          placeholder={placeholder}
          value={value}
          options={options}
          groups={groups}
          onValueChange={isMobile ? EMPTY_HANDLER : onValueChange}
          className={className}
          inputProps={{
            'aria-labelledby': id,
          }}
        />
        {isMobile && (
          <select
            className="native-simple-select"
            {...this.getNativeSelectValueProps()}
            onChange={this.onNativeSelectOptionChanged}
            ref={this.onNativeSelectRef}
          >
            {groups
              ? this.getNativeGroupedOptions().map(({ title, options: groupOptions }) => (
                  <optgroup label={title}>
                    {groupOptions.map(({ value: optionValue, label }) => (
                      <option value={optionValue}>{label}</option>
                    ))}
                  </optgroup>
                ))
              : [<option value="">Select an option</option>].concat(
                  options.map(({ value: optionValue, label }) => (
                    <option value={optionValue}>{label}</option>
                  )),
                )}
          </select>
        )}
      </div>
    );
  }
}
WorkflowSimpleSelect.defaultProps = {
  isUnder: false,
  value: '',
  onFocus: EMPTY_HANDLER,
  onBlur: EMPTY_HANDLER,
  innerRef: EMPTY_HANDLER,
  className: '',
  placeholderClassName: '',
  focusedClassName: 'adv-workflow-input-placeholder-focused',
  isLeftAligned: false,
  id: undefined,
};

WorkflowSimpleSelect.propTypes = {
  className: PropTypes.string,
  placeholderClassName: PropTypes.string,
  focusedClassName: PropTypes.string,
  value: PropTypes.number,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onValueChange: PropTypes.func.isRequired,
  innerRef: PropTypes.func,
  isLeftAligned: PropTypes.bool,
  placeholder: PropTypes.string.isRequired,
  isUnder: PropTypes.bool,
  id: PropTypes.string,
  alignment: PropTypes.oneOf([LEFT, CENTER, RIGHT]).isRequired,
  viewport: PropTypes.shape({ viewport: PropTypes.shape({ isMobile: PropTypes.bool }) }).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  groups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default withResponsiveness(WorkflowSimpleSelect);
